import { useEffect, useState } from 'react';
import { useUsers } from '../store/userStore';
import { AdminUser } from '../lib/api';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'customer', label: 'Customer' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'admin', label: 'Admin' },
];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function Users() {
  const {
    users,
    total,
    isLoading,
    error,
    roleFilter,
    searchQuery,
    setRoleFilter,
    setSearchQuery,
    fetchUsers,
    approveVendor,
    rejectVendor,
    deleteUser,
    updateUser,
  } = useUsers();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers().catch((e: Error) => toast.error(e.message));
  }, [fetchUsers, roleFilter, searchQuery]);

  const handleApprove = async (user: AdminUser) => {
    setPendingId(user.id);
    try {
      await approveVendor(user.id);
      toast.success(`${user.name} approved as vendor`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  const handleReject = async (user: AdminUser) => {
    setPendingId(user.id);
    try {
      await rejectVendor(user.id);
      toast.success(`Vendor application for ${user.name} rejected`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async (user: AdminUser) => {
    if (!window.confirm(`Delete user "${user.name}" permanently?`)) return;
    setPendingId(user.id);
    try {
      await deleteUser(user.id);
      toast.success('User deleted');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  const handleRoleChange = async (user: AdminUser, role: string) => {
    try {
      await updateUser(user.id, { role });
      toast.success('Role updated');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="container">
      <h1 className="page-title">User Management</h1>
      <p className="page-subtitle">
        {total} registered user{total === 1 ? '' : 's'}.
      </p>

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search by name or email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearchQuery(searchInput.trim());
          }}
        />
        <select value={roleFilter || ''} onChange={(e) => setRoleFilter(e.target.value || null)}>
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => setSearchQuery(searchInput.trim())}
        >
          Search
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}

      {isLoading && users.length === 0 ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : users.length === 0 ? (
        <div className="panel">
          <p className="empty-state">No users found.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Vendor Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                    >
                      <option value="customer">customer</option>
                      <option value="vendor">vendor</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    {user.role === 'vendor' ? (
                      user.is_vendor_approved ? (
                        <span className="status-badge status--approved">approved</span>
                      ) : (
                        <span className="status-badge status--pending">pending</span>
                      )
                    ) : (
                      <span className="status-badge">—</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="actions">
                      {user.role === 'vendor' && !user.is_vendor_approved && (
                        <>
                          <button
                            type="button"
                            className="btn btn--sm btn--primary"
                            disabled={pendingId === user.id}
                            onClick={() => handleApprove(user)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            disabled={pendingId === user.id}
                            onClick={() => handleReject(user)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={pendingId === user.id}
                        onClick={() => handleDelete(user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Users;
